import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const MEMBER_TOKEN_KEY = "twr_member_token";

const memberApi = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });

const stored = typeof window !== "undefined" ? localStorage.getItem(MEMBER_TOKEN_KEY) : null;
if (stored) memberApi.defaults.headers.common["Authorization"] = `Bearer ${stored}`;

export function setMemberToken(token) {
  if (token) {
    memberApi.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    localStorage.setItem(MEMBER_TOKEN_KEY, token);
  } else {
    delete memberApi.defaults.headers.common["Authorization"];
    localStorage.removeItem(MEMBER_TOKEN_KEY);
  }
}

const EMPTY = { account: null, member: null, application: null, isMember: false };
const MemberAuthCtx = createContext(null);

export function MemberAuthProvider({ children }) {
  const [auth, setAuth] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem(MEMBER_TOKEN_KEY);
    if (!token) { setAuth(EMPTY); setLoading(false); return; }
    try {
      const r = await memberApi.get("/account/me");
      setAuth({
        account: r.data.account,
        member: r.data.member,
        application: r.data.application,
        isMember: r.data.is_member,
      });
    } catch (e) {
      setMemberToken(null);
      setAuth(EMPTY);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const r = await memberApi.post("/account/login", { email, password });
    setMemberToken(r.data.token);
    await refresh();
    return r.data;
  };

  const register = async (full_name, email, password) => {
    const r = await memberApi.post("/account/register", { full_name, email, password });
    setMemberToken(r.data.token);
    await refresh();
    return r.data;
  };

  const logout = () => { setMemberToken(null); setAuth(EMPTY); };

  const idStatus = auth.account?.id_verification?.status || "none";

  return (
    <MemberAuthCtx.Provider value={{ ...auth, idStatus, loading, login, register, logout, refresh, api: memberApi }}>
      {children}
    </MemberAuthCtx.Provider>
  );
}

export function useMemberAuth() {
  const v = useContext(MemberAuthCtx);
  if (!v) throw new Error("useMemberAuth must be inside MemberAuthProvider");
  return v;
}

export { memberApi };
